import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2L PR 3 — Live Viewers pill.
 *
 * Polls /api/arenax/matches/{id}/live-viewers every 15s and renders a
 * pulsing chip when the count is above the threshold.  Avoids artificial
 * inflation — only renders when there are real viewers.
 *
 *   <ArenaxLiveViewers matchId="..." threshold={1} />
 */
export default function ArenaxLiveViewers({ matchId, threshold = 3, compact = false }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!matchId) return;
    let isMounted = true;
    const fetchCount = () => {
      api.get(`/arenax/matches/${matchId}/live-viewers`)
        .then(r => { if (isMounted) setCount(r.data?.viewers || 0); })
        .catch(() => {});
    };
    fetchCount();
    const tid = setInterval(fetchCount, 15000);
    return () => { isMounted = false; clearInterval(tid); };
  }, [matchId]);

  if (count < threshold) return null;

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-200"
        data-testid={`arenax-live-viewers-compact-${matchId}`}>
        <Eye className="h-2.5 w-2.5" />
        <span className="tabular-nums">{count}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30"
      data-testid={`arenax-live-viewers-${matchId}`}>
      <span className="relative inline-block w-1.5 h-1.5">
        <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-75" />
        <span className="absolute inset-0 rounded-full bg-cyan-400" />
      </span>
      <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-200 tabular-nums">
        {count} watching
      </span>
    </span>
  );
}
